import { formatDayShort } from "@/lib/format-utils";
import { Button, Loader, Popover } from "@mantine/core";
import { IconCalendar } from "@tabler/icons-react";
import { DatePicker } from '@mantine/dates';
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { isRangeService } from "@/lib/models-utils";
import dayjs from "dayjs";


interface Props {
    serviceCategory: string;
    value: [string | null, string | null];
    onChange: (value: [string | null, string | null]) => void;
    noPopover?: boolean;
    limits?: [string | null, string | null];
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl';
    label?: string;
    className?: string;
    disabled?: boolean;
    loading?: boolean;
    icon?: boolean;
}

export default function ServiceDatePopover({
    serviceCategory,
    limits,
    value,
    onChange,
    noPopover = false,
    size = 'sm',
    label,
    className,
    disabled = false,
    loading = false,
    icon = false,
}: Props) {
    const { t } = useTranslation();
    const [opened, setOpened] = useState(false);
    const [startDay, setStartDay] = useState(value?.[0]);
    const [endDay, setEndDay] = useState(value?.[1]);
    const isRange = isRangeService(serviceCategory);

    const handleClose = () => {
        onChange([startDay, endDay]);
        setOpened(false);
    }

    const handleDelete = () => {
        onChange([null, null]);
        setOpened(false);
    }

    const handleChange = (value: string | [string | null, string | null]) => {
        if (!isRange) {
            setStartDay(value as string);
            setEndDay(value as string);
            return;
        } else {
            setStartDay(value[0]);
            setEndDay(value[1]);
        }
    }
    useEffect(() => {
        if (isRange) {
            setStartDay(value?.[0]);
            setEndDay(value?.[1]);
        } else {
            setStartDay(value?.[0]);
            setEndDay(value?.[0]);
        }
    }, [value?.[0], value?.[1], isRange]);

    const Content = () => (
        <>
            <div>
                <DatePicker
                    highlightToday={true}
                    numberOfColumns={1}
                    type={isRange ? "range" : "default"}
                    value={isRange ? [startDay, endDay] : startDay}
                    onChange={handleChange}
                    minDate={limits?.[0] ? dayjs(limits[0]).toDate() : undefined}
                    maxDate={limits?.[1] ? dayjs(limits[1]).toDate() : undefined}
                    defaultDate={dayjs(value?.[0] || limits?.[0] || new Date()).toDate()}
                />
            </div>
            <div className="mt-2 flex items-center gap-12 w-full justify-between">
                <Button
                    color="black"
                    size="xs"
                    onClick={handleDelete}
                    variant="subtle"
                >
                    {t('delete_dates')}
                </Button>
                <Button
                    color="black"
                    size="xs"
                    radius="xl"
                    onClick={handleClose}
                >
                    {t('accept')}
                </Button>
            </div>
        </>
    )

    if (noPopover) {
        return <Content />
    }

    return (
        <Popover
            withArrow
            shadow="xl"
            radius="md"
            onChange={setOpened}
            opened={opened}
            closeOnClickOutside
            closeOnEscape
            clickOutsideEvents={['mousedown', 'touchstart']}
            keepMounted={false}
        >
            <Popover.Target>
                <div
                    className={`
                        ${className}
                        ${disabled ? 'cursor-default font-light' : 'cursor-pointer'}
                        text-${size}
                        space-y-0.5
                    `}
                    onClick={() => !disabled && setOpened(!opened)}
                >
                    {label && <label>{label}</label>}

                    {/* start date, always */}
                    <div className="flex items-center gap-1 whitespace-nowrap">
                        {
                            loading
                                ? <Loader size={16} color="black" />
                                : <IconCalendar size={12} className="text-gray-500 shrink-0" stroke={1} />
                        }
                        <div className={`whitespace-nowrap ${startDay ? 'font-bold' : 'font-light'}`}>
                            {formatDayShort(startDay) || '-- / ---'}
                        </div>
                    </div>
                    {isRange && (
                        <div className="pl-2 flex items-center gap-1 whitespace-nowrap">
                            <div className="text-gray-500 font-light text-xss">→</div>
                            <div className="font-light">
                                {formatDayShort(endDay) || '-- / ---'}
                            </div>
                        </div>
                    )}
                </div>
            </Popover.Target>
            <Popover.Dropdown>
                <Content />
            </Popover.Dropdown>
        </Popover>
    )
}